'use client'

import RichText from '@/components/RichText'
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipTrigger,
} from '@/components/ui/hybrid-tooltip'
import { cn } from '@/utilities/ui'

import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

/** Inner group fields (match the `citation` group in `config.ts`). */
export type CitationData = {
  shortened: string
  fullText: DefaultTypedEditorState
}

/**
 * One element of a Payload `hasMany` / array of groups: each row wraps the type as `citation`.
 */
export type CitationListItem = {
  id?: string | null
  citation: CitationData
}

type CitationProps = {
  citations: CitationListItem[] | undefined | null
}

export const Citation = ({ citations }: CitationProps) => {
  if (!citations || citations.length === 0) return null

  const first = citations[0].citation
  const triggerLabel = `${first.shortened}${citations.length > 1 ? ` +${citations.length - 1}` : ''}`

  return (
    <HybridTooltip>
      <HybridTooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-block min-w-0 text-left',
            'border border-dotted bg-transparent px-1 text-xs',
            'focus-visible:outline',
          )}
          aria-label="Show source"
        >
          {triggerLabel}
        </button>
      </HybridTooltipTrigger>
      <HybridTooltipContent
        side="top"
        sideOffset={6}
        className="max-w-[min(32rem,calc(100vw-2rem))] p-0 text-left text-foreground"
      >
        <div
          className="max-h-[min(24rem,70svh)] space-y-4 overflow-y-auto p-2 text-sm"
          data-lenis-prevent
        >
          {citations.map((row) => {
            const { citation } = row
            return (
              <div
                key={row.id ?? citation.shortened}
                className="border-b border-border pb-3 last:border-b-0 last:pb-0"
              >
                <RichText
                  data={citation.fullText}
                  enableGutter={false}
                  enableProse={false}
                  className="leading-6 text-neutral-700 [&_p+p]:mt-[1em]"
                />
              </div>
            )
          })}
        </div>
      </HybridTooltipContent>
    </HybridTooltip>
  )
}
